import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/content";
import { LogoMark } from "@/components/Logo";

// First-visit-only note on the CHECKOUT page about customs timelines + the 3-day refund window (from order placement).
const KEY = "rf_customs_note_seen";

export default function CustomsNotice() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = window.localStorage.getItem(KEY) === "1";
    } catch (e) {
      seen = false;
    }
    if (seen) return undefined;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      window.localStorage.setItem(KEY, "1");
    } catch (e) {
      // private mode — the note just shows again next visit, harmless
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="customs-notice-backdrop"
          onClick={close}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/80 px-4 backdrop-blur-sm"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="A note before you order"
            data-testid="customs-notice-dialog"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-[86vh] w-full max-w-md overflow-y-auto border border-line bg-surface p-7 shadow-[0_30px_80px_rgba(0,0,0,0.6)] sm:p-10"
          >
            <button
              type="button"
              data-testid="customs-notice-close"
              aria-label="Close note"
              onClick={close}
              className="absolute right-4 top-4 p-1 text-bone/40 transition-colors hover:text-gold"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>

            <span className="flex h-10 w-10 rotate-45 items-center justify-center border border-gold/60" aria-hidden="true">
              <LogoMark className="h-5 w-5 -rotate-45" />
            </span>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              A note before you order
            </p>
            <h2 className="mt-3 font-display text-3xl leading-[0.95] tracking-[0.01em] text-bone sm:text-4xl">
              THANK YOU FOR BEING HERE.
            </h2>

            <div className="mt-5 space-y-4 text-sm leading-relaxed text-bone/70">
              <p>
                Every piece you see on this page was sourced and checked by hand before it
                earned its place. Since our pieces travel across borders to reach you,
                customs and duties can sometimes slow the journey — your order may take
                anywhere from <span className="text-bone">20 days to 1 month</span> to arrive.
              </p>
              <p>
                We won't pretend that's fast. What we can promise is that we'll push your
                order through as quickly as we possibly can, and keep you posted at every
                step of the way.
              </p>
            </div>

            <div className="mt-6 border border-gold/40 p-5" data-testid="customs-notice-refund-box">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                Not happy? No drama.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-bone/70">
                If something isn't right, you have a{" "}
                <span className="text-bone">3-day refund window from the day you place your
                order</span> — just let us know on Instagram{" "}
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="customs-notice-instagram-link"
                  className="text-gold underline decoration-gold/50 underline-offset-4 hover:text-bone"
                >
                  {INSTAGRAM_HANDLE}
                </a>{" "}
                and we'll take care of you.
              </p>
            </div>

            <button
              type="button"
              data-testid="customs-notice-cta"
              onClick={close}
              className="mt-7 w-full bg-gold py-3.5 font-display text-lg tracking-[0.15em] text-ink transition-colors hover:bg-bone"
            >
              GOT IT — HAPPY SHOPPING
            </button>
            <p className="mt-4 text-center">
              <Link
                to="/shipping"
                data-testid="customs-notice-policy-link"
                onClick={close}
                className="font-mono text-[9px] uppercase tracking-[0.25em] text-bone/40 underline decoration-bone/30 underline-offset-4 hover:text-gold"
              >
                Read shipping &amp; returns
              </Link>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
